// Social login (SSO) helpers. Contract: backend/docs/sso.md.
import type { SsoProvider, SsoProviderId } from "./types";

export const SSO_PROVIDER_IDS: readonly SsoProviderId[] = ["google", "github", "microsoft", "oidc"];

const DEFAULT_NAMES: Record<SsoProviderId, string> = {
  google: "Google",
  github: "GitHub",
  microsoft: "Microsoft",
  oidc: "SSO",
};

export function isSsoProviderId(id: unknown): id is SsoProviderId {
  return typeof id === "string" && (SSO_PROVIDER_IDS as readonly string[]).includes(id);
}

/**
 * Only same-app relative paths are accepted as post-login destinations: a
 * single leading "/", never "//" (protocol-relative), no backslash (browsers
 * treat "/\" like "//") and no control characters. Anything else becomes "/".
 */
export function safeNext(next: string | null | undefined): string {
  if (!next || !next.startsWith("/") || next.startsWith("//")) return "/";
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(next)) return "/";

  return next;
}

/** The URL a provider button navigates to (a full page load, never fetch). */
export function ssoStartUrl(provider: SsoProviderId, next?: string | null): string {
  return `/api/v1/auth/sso/${encodeURIComponent(provider)}/start?next=${encodeURIComponent(safeNext(next))}`;
}

/** A link URL from POST me/identities/{p}/link must point at our own SSO start endpoint. */
export function isSsoLinkUrl(url: unknown): url is string {
  return typeof url === "string" && url.startsWith("/api/v1/auth/sso/") && !/[\\\s]/.test(url);
}

/**
 * The provider buttons to render: known ids only, in the server's order,
 * without duplicates. An undefined or malformed response renders no buttons,
 * so the email form keeps working when the providers request fails.
 */
export function visibleSsoProviders(data: unknown): SsoProvider[] {
  if (!Array.isArray(data)) return [];

  const seen = new Set<string>();
  const out: SsoProvider[] = [];
  for (const p of data as unknown[]) {
    if (!p || typeof p !== "object") continue;
    const { id, name } = p as { id?: unknown; name?: unknown };
    if (!isSsoProviderId(id) || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, name: typeof name === "string" && name.trim() ? name.trim() : DEFAULT_NAMES[id] });
  }

  return out;
}

/** Display name of a provider: the configured one (e.g. "Okta") or the default. */
export function ssoProviderName(id: string, providers?: readonly SsoProvider[] | null): string {
  const configured = providers?.find((p) => p.id === id)?.name;
  if (configured) return configured;

  return isSsoProviderId(id) ? DEFAULT_NAMES[id] : id;
}

/** Shown for a suspended account (password login 403 ACCOUNT_SUSPENDED and `sso_error=suspended`). */
export const SUSPENDED_ACCOUNT_MESSAGE = "This account is suspended. Contact support.";

export type SsoErrorCode =
  | "cancelled"
  | "state"
  | "email_unverified"
  | "account_exists"
  | "registration_disabled"
  | "already_linked"
  | "suspended"
  | "provider";

/**
 * A message for a `?sso_error=` code. The code comes from the URL, so it is
 * never echoed: unknown values get the generic message.
 */
export function ssoErrorMessage(code: string, provider?: string | null): string {
  const p = provider || null;

  switch (code) {
    case "cancelled":
      return p ? `Sign-in with ${p} was cancelled.` : "Sign-in was cancelled.";
    case "state":
      return "Your sign-in attempt expired or was already used. Please try again.";
    case "email_unverified":
      return `${p ?? "The provider"} didn't confirm that your email address is verified. Verify it there, or use your email and password.`;
    case "account_exists":
      return `An account with this email already exists. Sign in with your password, then connect ${p ?? "this provider"} in Account settings.`;
    case "registration_disabled":
      return "New sign-ups are closed on this Postflow server. Ask an administrator for an invitation.";
    case "suspended":
      return SUSPENDED_ACCOUNT_MESSAGE;
    case "already_linked":
      return `This ${p ?? "provider"} account is already connected to another Postflow user.`;
    default:
      return p
        ? `Something went wrong while signing in with ${p}. Please try again.`
        : "Something went wrong while signing in. Please try again.";
  }
}

// The callback URL only carries the error code, so the clicked provider is
// remembered for this tab to name it in the message. Storage may be blocked.
const LAST_PROVIDER_KEY = "pf_sso_provider";

export function rememberSsoProvider(id: SsoProviderId) {
  try {
    sessionStorage.setItem(LAST_PROVIDER_KEY, id);
  } catch {
    // ignore: the message falls back to a generic provider name
  }
}

export function recalledSsoProvider(): SsoProviderId | null {
  try {
    const id = sessionStorage.getItem(LAST_PROVIDER_KEY);

    return isSsoProviderId(id) ? id : null;
  } catch {
    return null;
  }
}
